import React, { useEffect } from 'react';
import { Amplify } from 'aws-amplify';
import PropTypes from 'prop-types';
import {
  ApolloClient as ApolloClientNative,
  InMemoryCache,
  ApolloProvider,
  createHttpLink,
  Observable,
  ApolloLink,
} from '@apollo/client';
import { createNetworkStatusNotifier } from 'react-apollo-network-status';
import { Backdrop, CircularProgress } from '@mui/material';
import { onError } from '@apollo/client/link/error';
import axios from 'axios';
import store from 'lib/store';
import config, { setAuthorization } from './aws-exports.js';
import { isEmpty } from '../validators.js';

// Initialize Amplify configuration
Amplify.configure(config);

// Create network status notifier for Apollo
const { link, useApolloNetworkStatus } = createNetworkStatusNotifier();

// Function to retrieve access token from cookies or passed token
const getAccessTokenHeader = (accessToken) => {
  const headers = {};
  try {
    let token = accessToken;
    // If no token is provided, attempt to retrieve from cookies
    if (!token) {
      const value = `; ${document?.cookie}`;
      const parts = value.split('; accessToken=');
      if (parts.length === 2) {
        token = parts.pop().split(';').shift();
      }
    }
    // If a token is found, add it to headers
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  } catch (e) {
    // Log error if token retrieval fails
    // eslint-disable-next-line no-console
    console.log('Failed to get Access Token cookie');
  }
  return headers;
};

let loader = false;

// Define the HTTP link for GraphQL endpoint
const httpLink = link.concat(
  createHttpLink({
    uri: config.API.GraphQL.endpoint,
  }),
);

// Authentication link to handle token-based authentication and refresh token flow
const authLink = new ApolloLink(async (operation, forward) => {
  const token = await store.get('accessToken');
  const refTk = await store.get('refreshToken');
  const header = { 'X-Api-Key': config.API.GraphQL.apiKey };

  // If access token exists, set it in the headers for the operation
  if (!isEmpty(token)) {
    header.Authorization = `Bearer ${token}`;
    operation.setContext(() => ({
      headers: {
        ...header,
      },
    }));
  } else if (!isEmpty(refTk)) {
    // If no access token, attempt to refresh the token using the refresh token
    const observable = new Observable((observer) => {
      (async () => {
        try {
          loader = true;
          // Make an API call to refresh the token
          await axios
            .post(
              `${process.env.MS_API_GATEWAY_URL}/api/iam/v1/auth/refreshToken`,
              {
                refreshToken: `Bearer ${refTk}`,
              },
            )
            .then(async (res) => {
              // Save new access and refresh tokens
              await store.save(
                'accessToken',
                res?.data?.accessToken.replace('Bearer ', ''),
                new Date(
                  new Date().getTime() + res?.data?.accessTokenMaxAge * 1000,
                ),
              );
              await store.save(
                'refreshToken',
                res?.data?.refreshToken.replace('Bearer ', ''),
                new Date(
                  new Date().getTime() + res?.data?.refreshTokenMaxAge * 1000,
                ),
              );
              // Reload the page to apply the new tokens
              window?.location.reload();
            })
            .finally(() => {
              loader = false;
            })
            .catch(() => {
              store.delete('accessToken');
              store.delete('refreshToken');
            });
        } catch (errs) {
          observer.error(errs);
        }
      })();
    });

    return observable;
  } else {
    // If no tokens are found, just set the API key in the headers
    operation.setContext(() => ({
      headers: {
        ...header,
      },
    }));
  }

  return forward(operation);
});

// Error handling link to manage GraphQL errors (e.g., 401, 403)
const errorLink = onError(({ graphQLErrors }) => {
  if (graphQLErrors) {
    /* eslint-disable no-restricted-syntax */
    for (const err of graphQLErrors) {
      switch (err.errorType) {
        case '403':
        case '401':
          // Handle 401/403 errors (e.g., logout or token refresh)
          break;
        default:
          break;
      }
    }
  }
});

// Apollo Client configuration
export const ApolloClient = new ApolloClientNative({
  link: ApolloLink.from([authLink, errorLink, httpLink]),
  cache: new InMemoryCache(),
  defaultOptions: {
    query: {
      fetchPolicy: 'network-only',
    },
    watchQuery: {
      fetchPolicy: 'network-only',
    },
    mutate: {
      fetchPolicy: 'network-only',
    },
  },
});

// GraphQLProvider component to wrap around your application and provide Apollo Client context
const GraphQLProvider = ({ children, accessToken }) => {
  // Effect hook to set the authorization header on component mount
  useEffect(() => {
    setAuthorization(getAccessTokenHeader(accessToken));
  }, [getAccessTokenHeader(accessToken)]);

  // Track network status for loading indicators
  const status = useApolloNetworkStatus();

  return (
    <ApolloProvider client={ApolloClient}>
      {/* Show loading spinner when there are pending queries or when the token is being refreshed */}
      {(status.numPendingQueries > 0 || loader) && (
        <Backdrop
          sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open
        >
          <CircularProgress color='inherit' />
        </Backdrop>
      )}
      {children}
    </ApolloProvider>
  );
};

// Prop validation for GraphQLProvider component
GraphQLProvider.propTypes = {
  children: PropTypes.node,
  accessToken: PropTypes.string,
};

export default GraphQLProvider;
